import { useLocation, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import {
  ClinicalTrialInventoryFormType,
  ClinicalTrialInventoryType,
} from '@dynamika-shared/types/clinicalTrialInventory';
import { renderInventorySchema } from './components';
import { ClinicalTrialInventoryBaseItemTypeState } from 'src/pages/ClinicalTrial/Readings/ReadingInventory/components/ClinicalTrialInventoryTable';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { BottomConfirmation } from '@components/BottomConfirmation/BottomConfirmation';
import { camelCase } from 'lodash';
import { useEffect } from 'react';
import { twMerge } from '@utils/twMerge';

type BaseReadingInventoryItemFormState = {
  name: string;
  code: string;
  description: string;
};

export const ReadingInventoryItemForm = () => {
  const params = useParams();
  const FormSegment =
    renderInventorySchema[params.itemType as keyof typeof renderInventorySchema]
      .element;
  const segmentValues =
    renderInventorySchema[params.itemType as keyof typeof renderInventorySchema]
      .defaultValues;

  const location = useLocation();
  const { createClinicalTrialInventoryItem, updateClinicalTrialInventoryItem } =
    useClinicalTrialInventorySlice((_, actions) => actions);

  const state = location.state as ClinicalTrialInventoryBaseItemTypeState;

  const defaultValues = {
    ...state.data,
    ...(state.mode !== 'edit' && segmentValues),
  } as ClinicalTrialInventoryFormType[keyof ClinicalTrialInventoryFormType];

  const methods = useForm({ defaultValues });
  const { watch, setValue, formState, getValues, handleSubmit, reset } =
    methods;

  const { errors, touchedFields, isDirty } = formState;

  const onChange = (
    fieldName: keyof BaseReadingInventoryItemFormState,
    value: string
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
  };

  const onBlur = (
    fieldName: keyof BaseReadingInventoryItemFormState,
    value: string
  ) => {
    if (touchedFields[fieldName]) {
      onChange(fieldName, value);
    }
  };

  const onSubmit = () => {
    const values =
      getValues() as ClinicalTrialInventoryFormType[keyof ClinicalTrialInventoryFormType];

    // Normalize metadata: ensure it is always an object at the submission
    // boundary, regardless of what CodeEditor stored in form state.
    // Handles nested stringification by looping until the result is no longer
    // a string (recovers from multi-level corruption).
    if ('metadata' in values && typeof values.metadata === 'string') {
      let parsed: unknown = values.metadata;
      try {
        while (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
        (values as Record<string, unknown>).metadata = parsed;
      } catch {
        // Invalid JSON — keep as-is; the API will reject it
      }
    }

    if (
      'runnable' in values &&
      !values.runnable &&
      values.workflowName &&
      values.workflowType
    ) {
      values.workflowName = null;
      values.workflowType = null;
    }
    if (state.mode === 'create') {
      createClinicalTrialInventoryItem({
        type: camelCase(params.itemType) as keyof ClinicalTrialInventoryType,
        ctId: params.id as string,
        data: { ...values, code: values?.code.trim() },
      });
    } else if (state.mode === 'edit') {
      updateClinicalTrialInventoryItem({
        type: camelCase(params.itemType) as keyof ClinicalTrialInventoryType,
        ctId: params.id as string,
        itemId: params.itemId as string,
        data: values,
      });
    }
  };
  useEffect(() => {
    if (state.mode === 'create') {
      setValue('code', `${state.data.code} `, { shouldDirty: true });
    }
  }, []);
  return (
    <FormProvider {...methods}>
      <div className={'w-full p-3 max-h-[70vh] overflow-y-auto'}>
        <form
          id={'reading-inventory'}
          className={twMerge('grid grid-cols-2 gap-4', isDirty && 'pb-[15rem]')}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Input
            required
            value={watch('name')}
            name={'name'}
            onChange={e => onChange('name', e.target.value)}
            onBlur={e => onBlur('name', e.target.value)}
            data-testid={'item-name'}
            errors={errors.name?.message}
            label={'Name'}
            placeholder={'Name'}
            classNameInputWrapper='max-w-full'
            maxLength={255}
          />
          <Input
            disabled
            value={watch('code')}
            name={'code'}
            data-testid={'item-code'}
            label={'Code'}
            className={'!opacity-100'}
            classNameLabel={'opacity-100'}
            classNameInputWrapper='max-w-full'
            maxLength={255}
          />
          <div className={'col-span-2'}>
            <TextArea
              className='!resize-none'
              label='Description'
              placeholder='Description'
              value={watch('description')}
              rows={3}
              name='description'
              onChange={e => onChange('description', e.target.value)}
              onBlur={e => onBlur('description', e.target.value)}
              classNameInputWrapper='max-w-full'
            />
          </div>
          <FormSegment />
        </form>
        {isDirty && (
          <BottomConfirmation
            onCancel={() => {
              reset();
            }}
            onOk={() => {}}
            okText='Save'
            okProps={{
              htmlType: 'submit',
              form: 'reading-inventory',
            }}
            data-testid='formChangesConfirmationPanel'
          >
            You have made changes to the data. Do you want to save the changes?
          </BottomConfirmation>
        )}
      </div>
    </FormProvider>
  );
};
